/* Custom interaction events only; standard Shopify commerce events remain platform-owned. */
(() => {
  if (window.JomoAnalytics) return;

  function publish(name, data) {
    let attempted = false;
    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        const sent = window.Shopify.analytics.publish(`jomo:${name}`, data);
        attempted = true;
        sent?.catch?.(() => {});
      }
    } catch {}
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, data);
        attempted = true;
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, ...data });
        attempted = true;
      }
    } catch {}
    return attempted;
  }

  const officeEvents = new Set([
    'office_inquiry_cta_clicked', 'office_inquiry_started', 'office_inquiry_submitted',
    'office_contact_email_clicked', 'office_roi_started', 'office_roi_calculated',
    'office_roi_edited', 'office_roi_downloaded', 'office_roi_download_failed'
  ]);
  const officeSurfaces = new Set(['office_plants', 'office_plant_roi']);
  const officeCtaLocations = new Set(['hero', 'services', 'faq', 'calculator_results', 'other']);
  const deliveryEvents = new Set(['delivery_checker_opened', 'delivery_zone_checked']);
  const deliverySurfaces = new Set(['cart_page', 'cart_drawer', 'product_page', 'delivery_page', 'unknown']);
  const deliveryServices = new Set(['local', 'shipping', 'territory', 'outside', 'unknown']);
  const deliveryZones = new Set(['Toronto Core', 'Oakville', 'Mississauga', 'Brampton', 'Vaughan', 'Richmond Hill', 'Markham', 'Scarborough', 'Canada wide', 'Not found', 'Outside coverage']);
  const deliveryProvinces = new Set(['Newfoundland and Labrador', 'Prince Edward Island', 'Nova Scotia', 'New Brunswick', 'Quebec', 'Ontario', 'Manitoba', 'Saskatchewan', 'Alberta', 'British Columbia', 'Yukon', 'Northwest Territories', 'Nunavut', 'Not available']);
  const deliveryInputTypes = new Set(['address', 'postal_code', 'current_location']);
  const deliveryResultStatuses = new Set(['checked', 'address_not_found', 'check_error', 'location_unavailable', 'location_denied']);

  function deliveryContext() {
    let estimate;
    let selection;
    try {
      estimate = window.JomoDeliveryData?.getEstimate?.();
      selection = window.JomoDeliveryData?.getSelection?.();
    } catch {}
    const service = deliveryServices.has(estimate?.service) ? estimate.service : 'unknown';
    const zone = service === 'local' ? estimate?.zoneName
      : service === 'shipping' ? 'Canada wide' : service === 'unknown' ? 'Not found' : 'Outside coverage';
    return {
      location_source: selection ? 'selected' : service !== 'unknown' ? 'detected' : 'unknown',
      detected_delivery_service: service,
      detected_delivery_zone: deliveryZones.has(zone) ? zone : 'Not found',
      detected_province: deliveryProvinces.has(estimate?.provinceName) ? estimate.provinceName : 'Not available'
    };
  }

  function filterState(url) {
    const state = new Map();
    for (const [name, value] of url.searchParams) {
      if (!name.startsWith('filter.') || !value) continue;
      if (!state.has(name)) state.set(name, new Set());
      state.get(name).add(value);
    }
    return state;
  }
  let previousPath = window.location.pathname;
  let previousFilters = filterState(new URL(window.location.href));

  const startedVideos = new WeakSet();
  const completedVideos = new WeakSet();
  const pendingPlantOpens = new WeakSet();
  const seenSearchResults = new Set();

  function videoData(player) {
    const section = player.closest('.shopify-section');
    const sectionType = Array.from(section?.classList || []).find(name => name.startsWith('shopify-section--'));
    return {
      video_id: String(player.getAttribute('data-analytics-video-id') || '').slice(0, 120),
      provider: player.getAttribute('host') || 'shopify',
      section_type: sectionType?.replace('shopify-section--', '') || 'unknown',
      autoplay: player.hasAttribute('autoplay'),
      product_handle: window.location.pathname.match(/\/products\/([^/]+)/)?.[1] || ''
    };
  }

  function videoStarted(player) {
    if (!player || startedVideos.has(player)) return;
    startedVideos.add(player);
    publish('storefront_video_started', videoData(player));
  }

  function searchResultsReady() {
    const marker = document.querySelector('[data-analytics-search-results]');
    if (!marker) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.get('q')?.trim()) return;
    const count = Number(marker.getAttribute('data-analytics-search-results'));
    // Query is used in memory for deduplication only, never in event data or storage.
    const criteria = Array.from(url.searchParams).filter(([name]) => name === 'q' || name === 'type' || name.startsWith('filter.') || name.startsWith('options.'));
    criteria.sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
    const key = JSON.stringify([url.pathname, criteria]);
    if (count !== 0 || seenSearchResults.has(key)) return;
    seenSearchResults.add(key);
    publish('search_no_results', {
      surface: 'search_page',
      filtered: Array.from(url.searchParams.keys()).some(name => name.startsWith('filter.'))
    });
  }

  window.JomoAnalytics = {
    deliveryContext,

    deliveryEvent(name, data) {
      if (!deliveryEvents.has(name) || !deliverySurfaces.has(data?.surface)) return false;
      try {
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return false;
      } catch { return false; }
      const payload = {
        surface: data.surface,
        location_source: ['detected', 'selected', 'unknown'].includes(data.location_source) ? data.location_source : 'unknown',
        detected_delivery_service: deliveryServices.has(data.detected_delivery_service) ? data.detected_delivery_service : 'unknown',
        detected_delivery_zone: deliveryZones.has(data.detected_delivery_zone) ? data.detected_delivery_zone : 'Not found',
        detected_province: deliveryProvinces.has(data.detected_province) ? data.detected_province : 'Not available'
      };
      if (name === 'delivery_checker_opened') {
        if (!['info_icon', 'accordion', 'visible'].includes(data.open_trigger)) return false;
        payload.open_trigger = data.open_trigger;
      } else {
        if (!deliveryInputTypes.has(data.input_type) || !deliveryResultStatuses.has(data.result_status)) return false;
        Object.assign(payload, {
          input_type: data.input_type,
          result_status: data.result_status,
          coverage_found: data.coverage_found === true,
          delivery_service: deliveryServices.has(data.delivery_service) ? data.delivery_service : 'unknown',
          delivery_zone: deliveryZones.has(data.delivery_zone) ? data.delivery_zone : 'Not found',
          province: deliveryProvinces.has(data.province) ? data.province : 'Not available',
          postal_area: typeof data.postal_area === 'string' && /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ]$/.test(data.postal_area) ? data.postal_area : 'Not available',
          live_rates_found: data.live_rates_found === true,
          rate_count: Number.isSafeInteger(data.rate_count) && data.rate_count >= 0 ? data.rate_count : 0,
          detection_comparison: ['same_area', 'different_area', 'unknown'].includes(data.detection_comparison) ? data.detection_comparison : 'unknown'
        });
      }
      return publish(name, payload);
    },

    officeEvent(name, data) {
      // Only fixed context enters office events; never forward form data, URLs or errors.
      if (!officeEvents.has(name) || !officeSurfaces.has(data?.surface)) return false;
      try {
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === false) return false;
      } catch { return false; }
      const payload = { surface: data.surface };
      if (name === 'office_inquiry_cta_clicked') {
        if (!officeCtaLocations.has(data.cta_location)) return false;
        payload.cta_location = data.cta_location;
      }
      // True means an existing destination accepted the handoff, not confirmed receipt.
      return publish(name, payload);
    },

    waitlistOpened(component) {
      const variant = component.variant;
      if (!variant || variant.available || component.waitlistOpenTracked) return;
      component.waitlistOpenTracked = true;
      publish('back_in_stock_opened', {
        product_id: String(component.dataset.productId || variant.product_id || ''),
        product_handle: String(component.dataset.productHandle || ''),
        variant_id: String(variant.id || '')
      });
    },

    filtersApplied(url) {
      const current = filterState(url);
      const previous = url.pathname === previousPath ? previousFilters : new Map();
      // Commit before publishing: analytics exceptions must never produce duplicates.
      previousFilters = current;
      previousPath = url.pathname;
      if (typeof document !== 'undefined') searchResultsReady();
      for (const name of new Set([...previous.keys(), ...current.keys()])) {
        const before = previous.get(name) || new Set();
        const after = current.get(name) || new Set();
        for (const value of new Set([...before, ...after])) {
          if (before.has(value) === after.has(value)) continue;
          publish('collection_filter_used', {
            filter_name: name.slice(0, 120),
            filter_value: value.slice(0, 120),
            action: after.has(value) ? 'applied' : 'removed',
            surface: /\/collections\//.test(url.pathname) ? 'collection' : 'search',
            collection_handle: url.pathname.match(/\/collections\/([^/]+)/)?.[1] || ''
          });
        }
      }
    },

    cartFailure({ operation, status = 0, message = '' }) {
      if (!['add', 'change', 'refresh_after_add'].includes(operation)) return;
      const code = Number(status) || 0;
      // A 422 alone does not establish an inventory error. Never transmit server text.
      const inventory = code === 422 && /sold out|out of stock|inventory|only .* (available|left)|can't add more|cannot add more|already .*cart/i.test(String(message));
      publish('cart_operation_failed', {
        operation,
        failure_type: inventory ? 'inventory' : code >= 400 && code < 500 ? 'rejected' : 'technical',
        http_status: code,
        surface: /\/cart\/?$/.test(window.location.pathname) ? 'cart_page' : 'storefront'
      });
    }
  };

  // Capture native non-bubbling media events, including dynamically inserted videos.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('play', event => {
      if (event.target.tagName === 'VIDEO') videoStarted(event.target.closest('video-media'));
    }, true);
    document.addEventListener('media:play', event => {
      if (event.target.matches?.('video-media[host]')) videoStarted(event.target);
    }, true);
    document.addEventListener('ended', event => {
      if (event.target.tagName !== 'VIDEO') return;
      const player = event.target.closest('video-media');
      if (!player || !startedVideos.has(player) || completedVideos.has(player)) return;
      completedVideos.add(player);
      publish('storefront_video_completed', videoData(player));
    }, true);
    document.addEventListener('click', event => {
      const summary = event.target.closest?.('summary');
      const details = summary?.parentElement;
      if (details?.tagName !== 'DETAILS' || details.open) return;
      if (details.closest('[data-analytics-plant-section]')) pendingPlantOpens.add(details);
    }, true);
    document.addEventListener('toggle', event => {
      const details = event.target;
      if (!pendingPlantOpens.has(details)) return;
      pendingPlantOpens.delete(details);
      if (!details.open) return;
      const section = details.closest('[data-analytics-plant-section]');
      if (!section) return;
      publish('plant_information_opened', {
        section: section.getAttribute('data-analytics-plant-section'),
        product_id: section.getAttribute('data-analytics-product-id') || '',
        topic: (details.querySelector('summary')?.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120)
      });
    }, true);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', searchResultsReady, { once: true });
    else searchResultsReady();
  }
})();
